import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { cacheSelections, getRepeatRiaInvestmentData } from '@api/add-funds';
import {
	RepeatRiaInvestmentMetadata,
	RepeatRiaInvestmentSelections,
	SuggestedInvestmentAmountOption,
	ValidRiaInvestmentRange
} from 'types/add-funds';
import { CheckoutMixpanelEvent } from 'types/checkout';
import { CheckoutOrderGroupDetails } from 'types/checkout';
import { createOrderGroup } from '@src/api/checkout';
import { investmentEntity } from './investment-entity';
import { OrderGroupResponse } from 'types/add-funds';
import { placeOrder } from '@api/checkout';
import { refreshJavaWebEntityInSession } from '@api/session';
import { ServiceLevelUpgradeName } from 'types/upgrade';
import store from '..';
import { upgrade } from './upgrade';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'addFunds',
	store
})
class AddFundsModule extends VuexModule {
	amount: string | null = null;
	cachedAddFundsSelections: RepeatRiaInvestmentSelections | null = null;
	forceInvestUpgrade = false;
	investUpgrade: boolean | null = null;
	paymentMethodId = '';
	suggestedInvestmentAmountOptions: Array<SuggestedInvestmentAmountOption> = [];
	reitInvestmentOrderGroupId = '';
	reitDocumentRevisionIds: Array<string> = [];
	validRiaInvestmentRange: ValidRiaInvestmentRange = {
		riaPlanId: '0',
		achMinInvestment: '0',
		achMaxInvestment: '0'
	};
	orderGroupResponse: OrderGroupResponse | null = null;
	bankAccountLinked = false;
	orderComplete = false;
	dataLoaded = false;
	amountAltText: string | null = null;

	get riaCheckoutMixpanelProperties(): CheckoutMixpanelEvent {
		const properties: CheckoutMixpanelEvent = {
			'Order Type': 'RIA',
			'RIA Plan': this.investUpgrade ? upgrade.newRiaPlan?.name || '' : investmentEntity.currentRiaPlanName
		};
		properties['Entity Type'] = investmentEntity.entityTypeLabel;
		properties['Transaction Type'] = 'ACH';

		if (this.reitInvestmentOrderGroupId && this.orderGroupResponse) {
			properties['Investment Amount'] = Number(this.amount);
			properties['Investments'] = this.orderGroupResponse.investments.map((investment) => {
				return investment.reitName;
			});
			if (this.bankAccountLinked) {
				properties['Payment Method Setup'] = 'New Account';
				properties['Payment Method Creation Method'] = 'Plaid';
			} else {
				properties['Payment Method Setup'] = 'Existing Account';
			}
		}

		return properties as CheckoutMixpanelEvent;
	}

	@Action({ rawError: true })
	public async fetchAndStoreRepeatInvestmentMetadata(): Promise<void> {
		const repeatRiaInvestmentMetadata = await getRepeatRiaInvestmentData();
		const serviceLevelUpgradeMappingData = repeatRiaInvestmentMetadata.serviceLevelUpgradeMapping;
		this.UPDATE_REPEAT_INVESTMENT_METADATA(repeatRiaInvestmentMetadata);
		upgrade.storeServiceLevelUpgradeData(serviceLevelUpgradeMappingData);
		this.UPDATE_DATA_LOADED(true);
	}

	@Action({ rawError: true })
	public clearPreviousOrder(): void {
		this.UPDATE_AMOUNT(null);
		this.UPDATE_PAYMENT_METHOD_ID('');
		this.UPDATE_REIT_DOCUMENT_REVISION_IDS([]);
		this.UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID('');
		this.UPDATE_ORDER_COMPLETE(false);
		if (!this.forceInvestUpgrade) {
			this.UPDATE_INVEST_UPGRADE(null);
		}
	}

	@Action({ rawError: true })
	public resetForceUpgrade(): void {
		this.UPDATE_FORCE_INVEST_UPGRADE(false);
	}

	@Action({ rawError: true })
	public async submitAgreements(reitDocumentRevisionIds: Array<string>): Promise<string> {
		this.UPDATE_REIT_DOCUMENT_REVISION_IDS(reitDocumentRevisionIds);
		return 'add-funds-ria-review';
	}

	@Action({ rawError: true })
	public async storeAndCacheSelections(selections: RepeatRiaInvestmentSelections): Promise<void> {
		this.UPDATE_AMOUNT(selections.amount);
		this.UPDATE_PAYMENT_METHOD_ID(selections.bankAccountId);
		this.UPDATE_INVEST_UPGRADE(selections.serviceLevelUpgradeSelected);
		await cacheSelections(selections);
	}

	@Action({ rawError: true })
	public async createOrder(orderDetails: {
		riaPlanId: string;
		serviceLevelUpgradeName: ServiceLevelUpgradeName | null;
	}): Promise<string> {
		const order: CheckoutOrderGroupDetails = {
			amount: this.amount ?? '0',
			paymentMethodId: this.paymentMethodId,
			riaPlanId: orderDetails.riaPlanId,
			serviceLevelUpgrade: orderDetails.serviceLevelUpgradeName
		};
		const orderGroupResponse = await createOrderGroup(order);
		this.UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID(orderGroupResponse.orderGroupId ?? '');
		this.UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse);
		return 'add-funds-ria-agreements';
	}

	@Action
	public parseDefaultAmountParam(defaultAmountQueryParam: string | null): void {
		if (defaultAmountQueryParam === 'minToUpgrade') {
			this.UPDATE_FORCE_INVEST_UPGRADE(true);
		}

		if (defaultAmountQueryParam !== null && !isNaN(Number(defaultAmountQueryParam))) {
			this.UPDATE_AMOUNT(defaultAmountQueryParam.toString());
		}
	}

	@Action({ rawError: true })
	public async submitReview(): Promise<string> {
		const orderGroupResponse = await placeOrder(
			this.reitInvestmentOrderGroupId,
			this.reitDocumentRevisionIds,
			null,
			null
		);

		this.UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse);
		await refreshJavaWebEntityInSession();

		this.UPDATE_ORDER_COMPLETE(true);

		if (this.investUpgrade) {
			await upgrade.postUpgradeActions();
		} else {
			await investmentEntity.refreshPositionsAndPerformance();
		}
		return 'add-funds-ria-success';
	}

	@Mutation
	public UPDATE_INVEST_UPGRADE(newVal: boolean | null): void {
		this.investUpgrade = newVal;
	}

	@Mutation
	public UPDATE_REIT_DOCUMENT_REVISION_IDS(reitDocumentRevisionIds: Array<string>): void {
		this.reitDocumentRevisionIds = reitDocumentRevisionIds;
	}

	@Mutation
	public UPDATE_REPEAT_INVESTMENT_METADATA(repeatRiaInvestmentMetadata: RepeatRiaInvestmentMetadata): void {
		this.validRiaInvestmentRange = repeatRiaInvestmentMetadata.validRiaInvestmentRange;
		this.suggestedInvestmentAmountOptions = repeatRiaInvestmentMetadata.suggestedInvestmentAmounts;
		this.cachedAddFundsSelections = repeatRiaInvestmentMetadata.repeatRiaInvestmentSelections ?? null;
		if (this.cachedAddFundsSelections !== null) {
			this.amount = this.cachedAddFundsSelections.amount;
			this.paymentMethodId = this.cachedAddFundsSelections.bankAccountId;
			if (!this.forceInvestUpgrade) {
				// when making transition from upgrade landing -> add funds, we want to ignore whatever you previously cached for investUpgrade and force it to true
				this.investUpgrade = this.cachedAddFundsSelections.serviceLevelUpgradeSelected;
			}
		}
	}

	@Mutation
	public UPDATE_AMOUNT(amount: string | null): void {
		this.amount = amount;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD_ID(paymentMethodId: string): void {
		this.paymentMethodId = paymentMethodId;
	}

	@Mutation
	public UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID(reitInvestmentOrderGroupId: string): void {
		this.reitInvestmentOrderGroupId = reitInvestmentOrderGroupId;
	}

	@Mutation
	public UPDATE_ORDER_COMPLETE(complete: boolean): void {
		this.orderComplete = complete;
	}

	@Mutation
	public UPDATE_FORCE_INVEST_UPGRADE(force: boolean): void {
		this.investUpgrade = force;
		this.forceInvestUpgrade = force;
	}

	@Mutation
	public UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse: OrderGroupResponse | null): void {
		this.orderGroupResponse = orderGroupResponse;
	}

	@Mutation
	public UPDATE_BANK_ACCOUNT_LINKED(bankAccountLinked: boolean): void {
		this.bankAccountLinked = bankAccountLinked;
	}

	@Mutation
	public UPDATE_DATA_LOADED(dataLoaded: boolean): void {
		this.dataLoaded = dataLoaded;
	}

	@Mutation
	public UPDATE_AMOUNT_ALT_TEXT(amountAltText: string): void {
		this.amountAltText = amountAltText;
	}
}

export const addFunds = getModule(AddFundsModule);
